import React from "react";
import Box from "@mui/material/Box";

import { SxProps, ThemeTextProps } from "merchant-portal-theme/ThemeTypes";
import { useTheme } from "merchant-portal-theme/useTheme";

type TextType = "body" | "h1" | "h2" | "h3" | "h5" | "inputLabel" | "overline";
type Color = "primary" | "mainGrey" | "disabledGrey" | "lightGrey";

export interface TextOptions {
	bold?: boolean;
	color?: Color;
	customOverride?: SxProps;
	fontSize?: string;
	type: TextType;
	underline?: boolean;
	pointer?: boolean;
	testId?: string;
}

export const textColorOptions: Record<Color, keyof ThemeTextProps> = {
	primary: "hoverText",
	mainGrey: "bodyAndIcons",
	lightGrey: "leftNavIconAndText",
	disabledGrey: "disabledText",
};

export const Text: React.FC<React.PropsWithChildren<TextOptions>> = ({
	children,
	bold,
	color,
	customOverride,
	fontSize,
	type = "body",
	underline,
	pointer,
	testId,
}) => {
	const theme = useTheme();

	const getElementType = (): React.ElementType => {
		switch (type) {
			case "h1":
				return "h1";
			case "h2":
				return "h2";
			case "h3":
				return "h3";
			case "h5":
				return "h5";
			case "inputLabel":
				return "label";
			case "overline":
			case "body":
			default:
				return "span";
		}
	};

	const getFontWeight = () => {
		if (bold === false) {
			return "400";
		}
		if (bold) {
			return "700";
		}
		switch (type) {
			case "h1":
			case "h2":
			case "h3":
				return "700";
			case "h5":
			case "body":
			case "inputLabel":
			case "overline":
			default:
				return "400";
		}
	};

	const getFontSize = () => {
		if (fontSize) return fontSize;
		switch (type) {
			case "h1":
				return "2.4rem";
			case "h2":
				return "2rem";
			case "h3":
				return "1.6rem";
			case "h5":
				return "1.2rem";
			case "overline":
				return "1.2rem";
			case "inputLabel":
			case "body":
			default:
				return "1.4rem";
		}
	};

	const getUnderline = () => {
		if (underline) return "underline";
		return "none";
	};

	const getTextColor = (overrideColor?: Color): keyof ThemeTextProps => {
		if (overrideColor) return textColorOptions[overrideColor];
		switch (type) {
			case "h1":
				return textColorOptions.primary;
			case "inputLabel":
			case "overline":
			case "h5":
				return textColorOptions.lightGrey;
			case "body":
			case "h2":
			case "h3":
			default:
				return textColorOptions.mainGrey;
		}
	};

	const getCursor = () => {
		if (pointer) return "pointer";
		return "inherit";
	};

	const styles = {
		fontWeight: getFontWeight(),
		fontSize: getFontSize(),
		color: theme.text[getTextColor(color)],
		cursor: getCursor(),
		textDecoration: getUnderline(),
		...customOverride,
	};

	return (
		<Box sx={styles} component={getElementType()} data-testid={testId || "text-component"}>
			{children}
		</Box>
	);
};
